<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue/types/jsx'

type Edge = 'top' | 'bottom' | 'start' | 'end'

const EDGE_TO_POSITIONAL_PROPERTY_MAP: Record<Edge, keyof CSSProperties> = {
  top: 'top',
  bottom: 'bottom',
  start: 'inset-inline-start',
  end: 'inset-inline-end',
}

const LINE_SIZE = 2 // px

const props = withDefaults(
  defineProps<{
    edge: Edge
    /**
     * The gap between draggable elements in pixels.
     * This value must match the actual spacing between draggable elements
     */
    gap?: number
    darkMode?: boolean | 'auto'
  }>(),
  {
    gap: 0,
    darkMode: 'auto',
  },
)

const isVertical = computed(() => props.edge === 'start' || props.edge === 'end')

const styles = computed<CSSProperties>(() => {
  const val = -(props.gap + LINE_SIZE) / 2
  const prop = EDGE_TO_POSITIONAL_PROPERTY_MAP[props.edge]
  return { [prop]: `${val}px` }
})
</script>

<template>
  <div
    :style="styles"
    :class="[
      // Styles for the line
      'absolute',
      'z-drop-indicator',
      {
        'inset-y-0': isVertical,
        'inset-x-0': !isVertical,
      },
      {
        'w-0.5': isVertical,
        'h-0.5': !isVertical,
      },
      'opacity-100',
      'pointer-events-none',
      {
        'bg-grape-500': darkMode === false,
        'bg-canary-500': darkMode === true,
        'bg-grape-500 dark:bg-canary-500': darkMode === 'auto',
      },

      // Styles for the starting dot
      `before:content-['']`,
      'before:absolute',
      {
        'before:-top-0.75': isVertical,
        'before:-start-0.5': isVertical,
        'before:-top-0.5': !isVertical,
        'before:-start-0.75': !isVertical,
      },
      'before:rounded-full',
      'before:w-1.5',
      'before:h-1.5',
      {
        'before:bg-grape-500': darkMode === false,
        'before:bg-canary-500': darkMode === true,
        'before:bg-grape-500 dark:before:bg-canary-500': darkMode === 'auto',
      },

      // Styles for the ending dot
      `after:content-['']`,
      'after:absolute',
      {
        'after:-bottom-0.75': isVertical,
        'after:-start-0.5': isVertical,
        'after:-bottom-0.5': !isVertical,
        'after:-end-0.75': !isVertical,
      },
      'after:rounded-full',
      'after:w-1.5',
      'after:h-1.5',
      {
        'after:bg-grape-500': darkMode === false,
        'after:bg-canary-500': darkMode === true,
        'after:bg-grape-500 dark:after:bg-canary-500': darkMode === 'auto',
      },
    ]"
  />
</template>
